import { useCallback, useMemo, useRef, useState } from 'react'
import {
  recordActionClick,
  updateActionClickStatus,
  type ActionSummaryData,
  type EnrichedAction,
  type SignalType,
  type ActionTypeEnum,
} from '../../services/recommendations_api'
import type {
  ActionClickStatus,
  ActionClickUpdateStatus,
} from '../../services/recommendations_contracts'

/**
 * Generate a unique key for an action.
 *
 * Prefers signal_fingerprint when available (globally unique per signal).
 * Falls back to deal_id-signal_type-action_type composite key.
 */
const getActionKey = (
  dealId: number | null,
  signalType: SignalType,
  actionType: ActionTypeEnum,
  signalFingerprint?: string | null
): string =>
  signalFingerprint
    ? signalFingerprint
    : `${dealId ?? 'no-deal'}-${signalType}-${actionType}`

interface UseActionClickManagerParams {
  /** Current summary data */
  summary: ActionSummaryData | null | undefined
}

interface UseActionClickManagerResult {
  /** Handler for when an action is clicked */
  handleActionClick: (
    action: EnrichedAction,
    signalType: SignalType,
    actionType: ActionTypeEnum
  ) => Promise<void>
  /** Handler for when a status is changed */
  handleStatusClick: (
    action: EnrichedAction,
    newStatus: ActionClickUpdateStatus,
    autoCompleted?: boolean
  ) => Promise<void>
  /** Get the existing status for an action */
  getStatusForAction: (action: EnrichedAction) => ActionClickStatus | null
  /** Get whether an action was auto-completed by the system */
  getAutoCompletedForAction: (action: EnrichedAction) => boolean
  /** Map of action keys to their current status */
  statusMap: Map<string, ActionClickStatus>
}

/**
 * Hook for managing action click tracking and status updates.
 *
 * Handles:
 * - Creating click records on first interaction
 * - Tracking click IDs by action key
 * - Status updates (done / dismissed)
 */
export function useActionClickManager({
  summary,
}: UseActionClickManagerParams): UseActionClickManagerResult {
  // Derive initial maps from existing user_clicks
  const {
    statusMap: initialStatusMap,
    initialAutoCompletedMap,
    initialClickIdMap,
  } = useMemo(() => {
    const statusMapInit = new Map<string, ActionClickStatus>()
    const autoCompletedMapInit = new Map<string, boolean>()
    const idMap = new Map<string, number>()
    if (summary?.user_clicks) {
      for (const click of summary.user_clicks) {
        const key = getActionKey(
          click.deal_id,
          click.signal_type,
          click.action_type,
          click.signal_fingerprint
        )
        idMap.set(key, click.click_id)
        if (click.status && click.status !== 'open') {
          statusMapInit.set(key, click.status)
          autoCompletedMapInit.set(key, click.auto_completed ?? false)
        }
      }
    }
    return {
      statusMap: statusMapInit,
      initialAutoCompletedMap: autoCompletedMapInit,
      initialClickIdMap: idMap,
    }
  }, [summary?.user_clicks])

  // Local overrides from user interactions this session.
  // Merged with server-derived maps (initialStatusMap, initialAutoCompletedMap)
  // at read time — no useEffect needed to sync server data into local state.
  const [localStatusOverrides, setLocalStatusOverrides] = useState<
    Map<string, ActionClickStatus>
  >(new Map())
  const [localAutoCompletedOverrides, setLocalAutoCompletedOverrides] =
    useState<Map<string, boolean>>(new Map())

  // Effective maps: server data wins for existing keys; local overrides
  // layer on top for optimistic updates from clicks made this session.
  const statusMap = useMemo(() => {
    const merged = new Map(initialStatusMap)
    for (const [k, v] of localStatusOverrides) merged.set(k, v)
    return merged
  }, [initialStatusMap, localStatusOverrides])

  const autoCompletedMap = useMemo(() => {
    const merged = new Map(initialAutoCompletedMap)
    for (const [k, v] of localAutoCompletedOverrides) merged.set(k, v)
    return merged
  }, [initialAutoCompletedMap, localAutoCompletedOverrides])

  // Tracks click record IDs: ref is the primary reader (avoids stale closure
  // issues); initialClickIdMap (always-current useMemo) is the server fallback.
  const clickIdMapRef = useRef<Map<string, number>>(new Map())
  const pendingClickCreationRef = useRef<Map<string, Promise<number | null>>>(
    new Map()
  )

  const setClickIdForKey = useCallback((key: string, clickId: number) => {
    clickIdMapRef.current = new Map(clickIdMapRef.current).set(key, clickId)
  }, [])

  const ensureClickIdForAction = useCallback(
    async (action: EnrichedAction): Promise<number | null> => {
      if (!summary || !summary.id) {
        return null
      }
      const summaryId = summary.id

      const key = getActionKey(
        action.deal_id,
        action.signal_type,
        action.action_type,
        action.signal_fingerprint
      )
      const existingClickId =
        clickIdMapRef.current.get(key) ?? initialClickIdMap.get(key)

      if (existingClickId) {
        return existingClickId
      }

      const existingPending = pendingClickCreationRef.current.get(key)
      if (existingPending) {
        return existingPending
      }

      const pendingPromise = (async () => {
        try {
          const response = await recordActionClick({
            action_summary_id: summaryId,
            signal_type: action.signal_type,
            action_type: action.action_type,
            deal_id: action.deal_id,
            contact_company_id: action.contact_company_id,
            action_text: action.action,
            recommendation_text: action.recommendation,
            signal_fingerprint: action.signal_fingerprint,
          })

          if (response?.click_id) {
            setClickIdForKey(key, response.click_id)
            return response.click_id
          }

          return null
        } catch (err) {
          console.error('Failed to record action click:', err)
          return null
        } finally {
          pendingClickCreationRef.current.delete(key)
        }
      })()

      pendingClickCreationRef.current.set(key, pendingPromise)
      return pendingPromise
    },
    [setClickIdForKey, summary]
  )

  // Helper to get existing status for an action.
  // Checks enriched_action-level resolution_status first (source of truth),
  // then falls back to the click-level status map.
  const getStatusForAction = useCallback(
    (action: EnrichedAction): ActionClickStatus | null => {
      // Enriched action resolution takes priority (set by LLM or user action)
      if (action.resolution_status) {
        return action.resolution_status.endsWith('_resolved')
          ? 'done'
          : 'dismissed'
      }
      const key = getActionKey(
        action.deal_id,
        action.signal_type,
        action.action_type,
        action.signal_fingerprint
      )
      return statusMap.get(key) ?? null
    },
    [statusMap]
  )

  const handleActionClick = useCallback(
    async (
      action: EnrichedAction,
      signalType: SignalType,
      actionType: ActionTypeEnum
    ) => {
      if (!summary || !summary.id) return

      try {
        const clickId = await ensureClickIdForAction({
          ...action,
          signal_type: signalType,
          action_type: actionType,
        })

        if (!clickId) {
          console.error('Failed to resolve click ID for action tracking')
        }
      } catch (err) {
        console.error('Failed to record action click:', err)
      }
    },
    [ensureClickIdForAction, summary]
  )

  const handleStatusClick = useCallback(
    async (
      action: EnrichedAction,
      newStatus: ActionClickUpdateStatus,
      autoCompleted: boolean = false
    ) => {
      if (!summary || !summary.id) return

      const key = getActionKey(
        action.deal_id,
        action.signal_type,
        action.action_type,
        action.signal_fingerprint
      )

      try {
        const existingClickId = await ensureClickIdForAction(action)

        if (existingClickId) {
          const response = await updateActionClickStatus({
            click_id: existingClickId,
            status: newStatus,
          })
          setLocalStatusOverrides((prev) => new Map(prev).set(key, newStatus))
          setLocalAutoCompletedOverrides((prev) =>
            new Map(prev).set(key, response?.auto_completed ?? autoCompleted)
          )
        } else {
          console.error('Cannot update status without a click ID')
          return
        }
      } catch (error) {
        console.error('Failed to update action status:', error)
      }
    },
    [ensureClickIdForAction, summary]
  )

  const getAutoCompletedForAction = useCallback(
    (action: EnrichedAction): boolean => {
      const key = getActionKey(
        action.deal_id,
        action.signal_type,
        action.action_type,
        action.signal_fingerprint
      )
      return autoCompletedMap.get(key) ?? false
    },
    [autoCompletedMap]
  )

  return {
    handleActionClick,
    handleStatusClick,
    getStatusForAction,
    getAutoCompletedForAction,
    statusMap,
  }
}

// Export getActionKey for use in other components if needed
export { getActionKey }
